import { produce } from 'immer'
import {
  SET_EXPENSE_RECEIPT_ATTACHMENT_IDS,
  SetExpenseReceiptAttachmentIdsAction,
} from 'src/app/UpdateExpenseFlyout/redux/updateExpense.actions'
import {
  ExpenseReportResponse,
  ExpenseResponse,
} from 'src/models/expenseReport.model'
import { updateExpenseRouteProps } from 'src/navigation/routeProps'
import {
  DELETE_EXPENSE_FAILURE,
  DELETE_EXPENSE_INIT,
  DELETE_EXPENSE_SUCCESS,
  DeleteExpenseActions,
  GET_EXPENSE_FAILURE,
  GET_EXPENSE_INIT,
  GET_EXPENSE_SUCCESS,
  GetExpenseActions,
  PATCH_EXPENSE_FAILURE,
  PATCH_EXPENSE_INIT,
  PATCH_EXPENSE_SUCCESS,
  PatchExpenseActions,
} from 'src/redux/actions/data/expenses.actions'
import {
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_INIT,
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentActions,
} from 'src/redux/actions/data/receiptAttachments.actions'
import { createFlyoutReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface UpdateExpenseState {
  expenseReport?: ExpenseReportResponse
  receiptAttachmentIds: string[]
  uploadingAttachmentThumbnails: Record<string, string>
  patchingExpense: boolean
  errorDetailMap?: ErrorDetailMap<string>
  topLevelError?: string
  loaded: boolean
  expense?: ExpenseResponse
  deletingExpense: boolean
  deletingExpenseTopLevelError?: string
  getExpenseTopLevelError?: string
}

export const initialState: UpdateExpenseState = {
  receiptAttachmentIds: [],
  uploadingAttachmentThumbnails: {},
  patchingExpense: false,
  loaded: false,
  deletingExpense: false,
}

export type Actions =
  | UploadAttachmentActions
  | SetExpenseReceiptAttachmentIdsAction
  | PatchExpenseActions
  | GetExpenseActions
  | DeleteExpenseActions

const CoreUpdateExpenseReducer = (
  state = initialState,
  action: Actions,
): UpdateExpenseState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case GET_EXPENSE_INIT: {
        draft.loaded = false
        return draft
      }

      case GET_EXPENSE_SUCCESS: {
        draft.loaded = true
        draft.expense = action.payload
        return draft
      }

      case GET_EXPENSE_FAILURE: {
        draft.loaded = true
        const errors = parseBackendErrors(action.payload)
        draft.getExpenseTopLevelError = errors.topLevelError
        return draft
      }
      case PATCH_EXPENSE_INIT: {
        draft.patchingExpense = true
        draft.errorDetailMap = undefined
        draft.topLevelError = undefined
        return draft
      }

      case PATCH_EXPENSE_SUCCESS: {
        draft.patchingExpense = false
        return draft
      }

      case PATCH_EXPENSE_FAILURE: {
        draft.patchingExpense = false
        const errors = parseBackendErrors(action.payload)
        draft.errorDetailMap = errors.errorDetailMap
        draft.topLevelError = errors.topLevelError
        return draft
      }

      case DELETE_EXPENSE_INIT: {
        draft.deletingExpense = true
        draft.deletingExpenseTopLevelError = undefined
        return draft
      }

      case DELETE_EXPENSE_SUCCESS: {
        draft.deletingExpense = false
        return draft
      }

      case DELETE_EXPENSE_FAILURE: {
        draft.deletingExpense = false
        const errors = parseBackendErrors(action.payload)
        draft.deletingExpenseTopLevelError = errors.topLevelError
        return draft
      }

      case SET_EXPENSE_RECEIPT_ATTACHMENT_IDS: {
        draft.receiptAttachmentIds = action.payload.ids
        return draft
      }

      case UPLOAD_ATTACHMENT_INIT: {
        draft.uploadingAttachmentThumbnails = {
          ...draft.uploadingAttachmentThumbnails,
          [action.payload.uploadId]: action.payload.thumbnailUrl,
        }
        return draft
      }

      case UPLOAD_ATTACHMENT_FAILURE: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        return draft
      }

      case UPLOAD_ATTACHMENT_SUCCESS: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        draft.receiptAttachmentIds = [
          ...draft.receiptAttachmentIds,
          action.payload.id,
        ]
        return draft
      }
    }
  })
}

const UpdateExpenseReducer = createFlyoutReducer(
  updateExpenseRouteProps,
  initialState,
  CoreUpdateExpenseReducer,
)

export default UpdateExpenseReducer
